import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { serviceJobsApi } from '@/services/api';
import type { CartItem } from '@/types';

export type ServiceJobDraft = {
  item: string;
  item_name: string;
  cartIndex: number;
  assigned_to: string;
  scheduled_at: string;
  serve_now: boolean;
  appointment_mode: string;
  staff_required: boolean;
};

type Props = {
  open: boolean;
  lines: CartItem[];
  onCancel: () => void;
  onConfirm: (drafts: ServiceJobDraft[]) => void;
};

function defaultDraft(line: CartItem, index: number): ServiceJobDraft {
  const mode = line.service_appointment || 'No Appointment';
  const staffRequired = !!line.service_staff_required;
  const serveNow = mode !== 'Required';
  return {
    item: line.item,
    item_name: line.item_name,
    cartIndex: index,
    assigned_to: line.service_assigned_to || '',
    scheduled_at: line.service_scheduled_at || '',
    serve_now: line.service_serve_now ?? serveNow,
    appointment_mode: mode,
    staff_required: staffRequired,
  };
}

/** Compact Serve Now / Schedule + staff picker before payment for Service lines. */
export default function ServiceCheckoutModal({ open, lines, onCancel, onConfirm }: Props) {
  const { t } = useTranslation();
  const serviceLines = useMemo(
    () => lines.map((line, index) => ({ line, index })).filter(({ line }) => line.item_type === 'Service'),
    [lines],
  );

  const [drafts, setDrafts] = useState<ServiceJobDraft[]>([]);
  const [error, setError] = useState('');

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['serviceStaff'],
    queryFn: async () => {
      const res = await serviceJobsApi.listStaff();
      return (res.data.message || []) as { user: string; full_name: string; role: string; role_label?: string }[];
    },
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setDrafts(serviceLines.map(({ line, index }) => defaultDraft(line, index)));
    setError('');
  }, [open, serviceLines]);

  if (!open) return null;

  const update = (i: number, patch: Partial<ServiceJobDraft>) => {
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };

  const submit = () => {
    for (const d of drafts) {
      if (d.staff_required && !d.assigned_to) {
        setError(t('services.staffRequiredError', 'Please assign staff for {{name}}', { name: d.item_name }));
        return;
      }
      if (d.appointment_mode === 'Required' && !d.scheduled_at) {
        setError(t('services.scheduleRequiredError', 'Please set date/time for {{name}}', { name: d.item_name }));
        return;
      }
      if (!d.serve_now && !d.scheduled_at) {
        setError(t('services.scheduleRequiredError', 'Please set date/time for {{name}}', { name: d.item_name }));
        return;
      }
    }
    onConfirm(drafts);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {t('services.checkoutTitle', 'Service details')}
          </h2>
          <button type="button" onClick={onCancel} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          )}
          {drafts.map((d, i) => (
            <div key={`${d.item}-${d.cartIndex}`} className="rounded-xl border border-gray-100 p-3 space-y-3">
              <p className="text-sm font-semibold text-gray-900">{d.item_name}</p>
              {d.appointment_mode !== 'No Appointment' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => update(i, { serve_now: true, scheduled_at: '' })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                      d.serve_now ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-200'
                    }`}
                    disabled={d.appointment_mode === 'Required'}
                  >
                    {t('services.serveNow', 'Serve Now')}
                  </button>
                  <button
                    type="button"
                    onClick={() => update(i, { serve_now: false })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                      !d.serve_now || d.appointment_mode === 'Required'
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-200'
                    }`}
                  >
                    {t('services.schedule', 'Schedule')}
                  </button>
                </div>
              )}
              {(!d.serve_now || d.appointment_mode === 'Required') && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('services.dateTime', 'Date & time')}
                  </label>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={d.scheduled_at}
                    onChange={(e) => update(i, { scheduled_at: e.target.value, serve_now: false })}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('services.assignStaff', 'Staff')}
                  {d.staff_required ? ' *' : ''}
                </label>
                <select
                  className="input-field"
                  value={d.assigned_to}
                  onChange={(e) => update(i, { assigned_to: e.target.value })}
                >
                  <option value="">{t('services.selectStaff', 'Select staff')}</option>
                  {staff.map((s) => (
                    <option key={s.user} value={s.user}>
                      {s.full_name} {s.role_label || s.role ? `(${s.role_label || s.role})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="p-4 border-t border-gray-100 flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 btn-secondary py-2.5">
            {t('common.cancel')}
          </button>
          <button type="button" onClick={submit} className="flex-1 btn-primary py-2.5">
            {t('services.continuePayment', 'Continue to payment')}
          </button>
        </div>
      </div>
    </div>
  );
}
